import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Full-screen, non-dismissable age verification gate.
 *
 * Steps: facial age estimation -> contact details + terms -> access code.
 * See age-gate-worker/README.md for the companion Cloudflare Worker this
 * component depends on for the age estimation step.
 */
class AgeGateModal extends HTMLElement {
  /** @type {MediaStream | null} */
  #stream = null;


  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.steps = {
      declaration: this.querySelector('[data-step="declaration"]'),
      camera: this.querySelector('[data-step="camera"]'),
      denied: this.querySelector('[data-step="denied"]'),
      contact: this.querySelector('[data-step="contact"]'),
      code: this.querySelector('[data-step="code"]'),
    };

    this.#bindDeclarationStep();
    this.#bindCameraStep();
    this.#bindContactStep();
    this.#bindCodeStep();
    this.#preventDismissal();

    requestAnimationFrame(() => {
      lockScroll(this.dialog);
      this.dialog.showModal();
    });
  }

  disconnectedCallback() {
    this.#stopCamera();
    if (this.dialog) unlockScroll(this.dialog);
  }

  #preventDismissal() {
    this.dialog.addEventListener('cancel', (event) => event.preventDefault());
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }

  #showStep(name) {
    for (const [key, el] of Object.entries(this.steps)) {
      el?.toggleAttribute('hidden', key !== name);
    }
  }

  #bindDeclarationStep() {
    const panel = this.steps.declaration;
    panel.querySelector('[data-declare-underage]').addEventListener('click', () => {
      this.#showStep('denied');
      setTimeout(() => location.reload(), 30000);
    });
    panel.querySelector('[data-declare-overage]').addEventListener('click', () => {
      this.#showStep('camera');
    });
  }

  #bindCameraStep() {
    const panel = this.steps.camera;
    const enableButton = panel.querySelector('[data-enable-camera]');
    const captureButton = panel.querySelector('[data-capture-photo]');
    const video = panel.querySelector('[data-camera-video]');
    const previewFrame = panel.querySelector('[data-camera-preview]');
    const errorEl = panel.querySelector('[data-camera-error]');
    const statusEl = panel.querySelector('[data-camera-status]');

    const showError = (message) => {
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    enableButton.addEventListener('click', async () => {
      errorEl.hidden = true;
      enableButton.disabled = true;

      try {
        this.#stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
        video.srcObject = this.#stream;
        previewFrame.hidden = false;
        enableButton.hidden = true;
        captureButton.hidden = false;
      } catch {
        enableButton.disabled = false;
        showError('Camera access is required to continue. Please allow camera permission and try again.');
      }
    });

    captureButton.addEventListener('click', async () => {
      captureButton.disabled = true;
      statusEl.hidden = false;
      errorEl.hidden = true;

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

      const image = canvas.toDataURL('image/jpeg', 0.85);

      this.#stopCamera();
      previewFrame.hidden = true;

      try {
        const result = await this.#verifyAge(image);

        if (result.pass) {
          this.#showStep('contact');
        } else if (result.reason === 'no_face') {
          statusEl.hidden = true;
          captureButton.disabled = false;
          captureButton.hidden = true;
          enableButton.hidden = false;
          enableButton.disabled = false;
          showError('No face detected. Please position your face clearly in the frame and try again.');
        } else {
          this.#showStep('denied');
        }
      } catch {
        statusEl.hidden = true;
        captureButton.disabled = false;
        captureButton.hidden = true;
        enableButton.hidden = false;
        enableButton.disabled = false;
        showError("We couldn't complete the age check. Please try again.");
      }
    });
  }

  #stopCamera() {
    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#stream = null;
  }

  /**
   * Sends the captured frame to the Cloudflare Worker proxy, which performs
   * the signed Yoti age-estimation call server-side. The image is discarded
   * client-side immediately after this call and never persisted.
   *
   * @param {string} image - A base64 data URL JPEG frame.
   * @returns {Promise<{pass: boolean, reason?: string}>}
   */
  async #verifyAge(image) {
    const response = await fetch(this.dataset.verifyEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image, minAge: Number(this.dataset.minAge) }),
    });

    if (!response.ok) throw new Error(`Verification request failed: ${response.status}`);

    return response.json();
  }

  #bindContactStep() {
    const panel = this.steps.contact;
    const form = panel.querySelector('[data-contact-form]');
    const nameInput = panel.querySelector('#AgeGateName');
    const emailInput = panel.querySelector('#AgeGateEmail');
    const termsCheckbox = panel.querySelector('[data-terms-checkbox]');
    const submitButton = panel.querySelector('[data-contact-submit]');
    const errorEl = panel.querySelector('[data-contact-error]');

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const syncSubmitState = () => {
      const valid = nameInput.value.trim().length > 0 && emailPattern.test(emailInput.value) && termsCheckbox.checked;
      submitButton.disabled = !valid;
    };

    for (const el of [nameInput, emailInput, termsCheckbox]) {
      el.addEventListener('input', syncSubmitState);
      el.addEventListener('change', syncSubmitState);
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitButton.disabled) return;

      submitButton.disabled = true;
      errorEl.hidden = true;

      try {
        await this.#createCustomerRecord(nameInput.value.trim(), emailInput.value.trim());
      } catch (error) {
        // Record-keeping is best-effort: don't block the visitor's access
        // over a CRM write failure.
        console.warn('age-gate: customer record creation failed', error);
      }

      this.#showStep('code');
    });
  }

  /**
   * Records the visitor's name/email as a Shopify customer for record-
   * keeping. This goes through the Cloudflare Worker (Admin API, server-
   * side) rather than Shopify's public storefront account-creation form —
   * stores using Shopify's newer hosted Customer Accounts redirect that
   * form to a full-page shopify.com login flow, which can't be submitted
   * silently from inside the modal.
   *
   * @param {string} fullName
   * @param {string} email
   */
  async #createCustomerRecord(fullName, email) {
    const endpoint = this.dataset.customerEndpoint;
    if (!endpoint) return;

    const [firstName, ...rest] = fullName.split(' ');
    const lastName = rest.join(' ') || firstName;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, email }),
    });

    if (!response.ok) throw new Error(`Customer record request failed: ${response.status}`);
  }

  #bindCodeStep() {
    const panel = this.steps.code;
    const codeDisplay = panel.querySelector('[data-access-code-display]');
    const copyButton = panel.querySelector('[data-copy-code]');
    const closeButton = panel.querySelector('[data-close-gate]');

    codeDisplay.textContent = this.dataset.accessCode || '';

    copyButton.addEventListener('click', async () => {
      await navigator.clipboard.writeText(this.dataset.accessCode || '');
      copyButton.textContent = 'Copied!';
      setTimeout(() => {
        copyButton.textContent = 'Copy code';
      }, 2000);
    });

    closeButton.addEventListener('click', () => {
      unlockScroll(this.dialog);
      this.dialog.close();
      this.remove();
    });
  }
}

if (!customElements.get('age-gate-modal')) customElements.define('age-gate-modal', AgeGateModal);
